import { useFrame } from '@react-three/fiber';
import { Billboard, useScroll } from '@react-three/drei';
import { Sparkles } from '../../Sparkles';
import * as React from 'react';
import * as THREE from 'three';
import { Cloud } from '../../Cloud';
import { Clouds } from '../../Clouds';
import GuidanceEditionBox from '../../GuidanceEditionBox';
import HeavensBridgeBox from '../../HeavensBridgeBox';
import { DECKS_CAMERA, DECKS_CLOUDS_POS } from '../constants';
import { makeSphericalDistribute } from '../utils';
import { makeDecksVisState, updateDecksVis } from '../decksVisibility';
import { PageTransitionContext } from '../PageTransitionContext';
import { useSceneScalars } from '../../../hooks/useSceneScalars';
import { useBreakpoint } from '../../../hooks/useBreakpoint';

const GUIDANCE_SCALE = 175;
const GUIDANCE_POSITION = new THREE.Vector3(-40, -615, 63);
const GUIDANCE_ROTATION_DEG: [number, number, number] = [-118, 2, 132];

const BOX_SCALE = 175;
const BOX_POSITION = new THREE.Vector3(83, -576, 215);
const BOX_ROTATION_DEG: [number, number, number] = [-125, -4, 130];

// Screen-x axis of the parked decks camera (unit vector ⊥ its view ray, in
// the ground plane): moving a box along it shifts it horizontally on screen
// without changing its distance to the camera. Used by the per-tier gather.
const DECKS_VIEW_RIGHT = (() => {
  const [px, py, pz] = DECKS_CAMERA.pos;
  const [tx, ty, tz] = DECKS_CAMERA.target;
  const dir = new THREE.Vector3(tx - px, ty - py, tz - pz);
  return new THREE.Vector3(-dir.z, 0, dir.x).normalize();
})();

// Screen-y axis of the same camera (what "up" looks like on screen at the
// parked decks pose). ⊥ to both the view ray and DECKS_VIEW_RIGHT.
const DECKS_VIEW_UP = (() => {
  const [px, py, pz] = DECKS_CAMERA.pos;
  const [tx, ty, tz] = DECKS_CAMERA.target;
  const forward = new THREE.Vector3(tx - px, ty - py, tz - pz).normalize();
  return new THREE.Vector3().crossVectors(DECKS_VIEW_RIGHT, forward).normalize();
})();

// Midpoint for sparkles & orbiting light
const CENTER = new THREE.Vector3(
  (GUIDANCE_POSITION.x + BOX_POSITION.x) / 2,
  (GUIDANCE_POSITION.y + BOX_POSITION.y) / 2,
  (GUIDANCE_POSITION.z + BOX_POSITION.z) / 2,
);

// Ambient motion
const FLOAT_SPEED = 0.5;
const FLOAT_AMPLITUDE = 2;
const ORBIT_RADIUS = 5;
const ORBIT_SPEED = 0.12;

// Soft warm radial gradient — used for the "halo" plane under each box.
// Wider on the horizontal axis so the resulting glow reads as an oval
// pool of light beneath the floating box rather than a perfect disc.
function makeBoxGlowTexture(): THREE.Texture {
  const size = 256;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const grad = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  grad.addColorStop(0.0, 'rgba(255, 245, 225, 0.32)');
  grad.addColorStop(0.25, 'rgba(245, 235, 235, 0.18)');
  grad.addColorStop(0.55, 'rgba(225, 230, 250, 0.10)');
  grad.addColorStop(0.8, 'rgba(220, 225, 250, 0.04)');
  grad.addColorStop(1.0, 'rgba(220, 225, 250, 0)');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, size, size);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

// Base plane size — used only to compute the scale ratio in useFrame
// (the live size & vertical offset come from the Leva panel).
const GLOW_PLANE: [number, number] = [240, 110];

// Reusable temp vector for per-frame camera-up extraction
// (used by the halo Billboard offset calculation).
const _camUp = new THREE.Vector3();

// Baked Leva values for the Decks lighting panel
// (was: useControls("Decks · Lighting") with ambient/hemi/key/fill/rim/halo/material folders).
// 3-point setup (ambient + hemi + directional key + fill point + rim point)
// layered on top of the global IBL (Environment preset="park" in Scene.tsx).
// envMapIntensity = 0 — boxes are lit purely by analytic lights, IBL contribution disabled.
const DECKS_LIGHTING = {
  ambientIntensity: 2.0,
  hemiIntensity: 0.6,
  hemiSky: '#c7c7c7',
  hemiGround: '#d5d5d5',
  keyIntensity: 2.15,
  keyColor: '#bbcdeb',
  keyX: -26,
  keyY: 278,
  keyZ: 6,
  fillIntensity: 1.4,
  fillColor: '#bcd2f0',
  fillX: -210,
  fillY: -60,
  fillZ: 140,
  fillDistance: 700,
  rimIntensity: 0.9,
  rimColor: '#ffc890',
  rimX: -60,
  rimY: 120,
  rimZ: -250,
  rimDistance: 600,
  haloOpacityMul: 2.1,
  haloEnabled: true,
  haloWidth: 265,
  haloHeight: 190,
  haloOffset: 131,
  envMapIntensity: 0,
} as const;

// Baked Leva values for the decks clouds (was: useControls("Decks · Clouds")).
// Seeded from the hero (HomePart) look, then tuned. opacityMul scales the
// active/inactive opacity; position & distribution stay decks-specific.
const DECKS_CLOUDS = {
  scale: 2.85,
  volume: 273,
  growth: 204,
  smallestVolume: 0.5,
  bounds: 355,
  opacityMul: 0.5,
  speed: 0.13,
  color: '#ffffff',
  seed: 3,
} as const;

// The decks framing is tuned for wide viewports. The vertical FOV is fixed,
// so a narrower window keeps the boxes' pixel size but loses width around
// them — at ~1200px the pair reads oversized. Shrink the boxes with the
// viewport aspect (width/height) below the reference so their horizontal
// share of the frame stays constant. Mobile is excluded: it has its own
// camera pullback + DECKS_BOX_SCALE_BY_TIER tier instead.
const DECKS_FIT_REF_ASPECT = 1.6;
const DECKS_FIT_MIN = 0.45;

// Sparkles drifting between the boxes — atmospheric depth.
const SPARKLE_COUNT = 60;
const SPARKLE_SCALE: [number, number, number] = [200, 120, 200];
const SPARKLE_COLOR = '#e8e0f5';
const SPARKLE_SPEED = 0.5;
const SPARKLE_OPACITY = 0.5;
const SPARKLE_SIZE = 3;

type Props = {
  spherical: Float32Array;
  segments: number;
  active: boolean;
};

export function DecksPart({ spherical, segments, active }: Props) {
  const scroll = useScroll();
  const scalars = useSceneScalars();
  const breakpoint = useBreakpoint();

  // Visibility window in t-space:
  //   t 8.65 → 8.75 — fade in (camera already parked at DECKS)
  //   t 8.75 → 9.25 — dwell, fully visible (the pause, as on /decks)
  //   t 9.25 → 9.75 — fade out (reviews HTML scrolls up over the decks scene)
  // Mirrors the appearance/disappearance pattern from /decks: scale 0→1→0
  // driven by easeOutQuart on each side.
  const ENTRY_T_START = scalars.thresholds.decksDwell;
  const ENTRY_T_END = scalars.thresholds.decksEnterEnd;
  const EXIT_T_START = scalars.thresholds.decksReviews;
  const EXIT_T_END = scalars.tMax;
  const REVERSE_EXIT_T = scalars.thresholds.decksReverseExit;

  const { isTransitioningRef } = React.useContext(PageTransitionContext);

  const guidanceRef = React.useRef<THREE.Group>(null);
  const boxRef = React.useRef<THREE.Group>(null);
  const guidanceGlowRef = React.useRef<THREE.Group>(null);
  const boxGlowRef = React.useRef<THREE.Group>(null);
  const guidanceGlowMatRef = React.useRef<THREE.MeshBasicMaterial>(null);
  const boxGlowMatRef = React.useRef<THREE.MeshBasicMaterial>(null);
  const keyLightRef = React.useRef<THREE.DirectionalLight>(null);
  const fillLightRef = React.useRef<THREE.PointLight>(null);
  const rimLightRef = React.useRef<THREE.PointLight>(null);
  const glowTex = React.useMemo(() => makeBoxGlowTexture(), []);

  const ctl = DECKS_LIGHTING;

  const cloud = DECKS_CLOUDS;

  // Decks box transforms are baked into the module consts above
  // (GUIDANCE_POSITION/ROTATION_DEG, BOX_POSITION/ROTATION_DEG).
  // was: useControls("Decks · Transform") with guidance/guidanceRot/box/boxRot
  // vector inputs + per-box axesHelper tuning aids.

  // Decks appear/disappear state — see decksVisibility.ts. Shared verbatim
  // with the HTML overlay (ScrollSections) so 3D and text stay in lockstep.
  const visState = React.useRef(makeDecksVisState());

  // Per-tier layout: slide each box `gather` world units inward along the
  // decks camera's screen-x axis (perpendicular to the view ray, so depth and
  // on-screen size stay put — only the portrait gap closes). Guidance sits on
  // the positive side of that axis, the Heavens Bridge box on the negative.
  const gather = scalars.decksBoxGather;
  // `level` slides the pair onto one visual line: the authored composition
  // renders the Guidance box (screen-right) higher and the Heavens Bridge
  // box (screen-left) lower, so positive level moves them down/up.
  const level = scalars.decksBoxLevel;
  const guidanceBasePos = React.useMemo(
    () =>
      GUIDANCE_POSITION.clone()
        .addScaledVector(DECKS_VIEW_RIGHT, -gather)
        .addScaledVector(DECKS_VIEW_UP, -level),
    [gather, level],
  );
  const boxBasePos = React.useMemo(
    () =>
      BOX_POSITION.clone()
        .addScaledVector(DECKS_VIEW_RIGHT, gather)
        .addScaledVector(DECKS_VIEW_UP, level),
    [gather, level],
  );

  const guidanceBaseRot = React.useMemo(
    () =>
      new THREE.Euler(
        THREE.MathUtils.degToRad(GUIDANCE_ROTATION_DEG[0]),
        THREE.MathUtils.degToRad(GUIDANCE_ROTATION_DEG[1]),
        THREE.MathUtils.degToRad(GUIDANCE_ROTATION_DEG[2]),
      ),
    [],
  );

  const boxBaseRot = React.useMemo(
    () =>
      new THREE.Euler(
        THREE.MathUtils.degToRad(BOX_ROTATION_DEG[0]),
        THREE.MathUtils.degToRad(BOX_ROTATION_DEG[1]),
        THREE.MathUtils.degToRad(BOX_ROTATION_DEG[2]),
      ),
    [],
  );

  // Disable scene fog on box materials so they match the decks page appearance
  React.useEffect(() => {
    for (const group of [guidanceRef.current, boxRef.current]) {
      if (!group) continue;
      group.traverse((child) => {
        if ((child as THREE.Mesh).isMesh) {
          const mesh = child as THREE.Mesh;
          const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
          for (const mat of mats) (mat as THREE.MeshStandardMaterial).fog = false;
        }
      });
    }
  });

  useFrame((state, delta) => {
    const rawT = scroll.offset * scalars.tScale;
    const t = Math.min(rawT, scalars.tMax);

    // updateDecksVis already returns an exponentially-smoothed value, kept
    // in lockstep with the HTML overlay (ScrollSections) which calls it too.
    const s = updateDecksVis(visState.current, t, delta, {
      entryStart: ENTRY_T_START,
      entryEnd: ENTRY_T_END,
      exitStart: EXIT_T_START,
      exitEnd: EXIT_T_END,
      reverseExitStart: REVERSE_EXIT_T,
    });
    // Per-tier shrink (mobile) plus aspect fit (tablet/desktop) so both boxes
    // fit narrow frames. Applied to the box/halo sizes only — lights and
    // motion keep `s`.
    const aspect = state.size.width / Math.max(1, state.size.height);
    const aspectFit =
      breakpoint === 'mobile'
        ? 1
        : Math.min(
            1,
            Math.max(DECKS_FIT_MIN, Math.sqrt(aspect / DECKS_FIT_REF_ASPECT)),
          );
    const sizeS = s * scalars.decksBoxScale * aspectFit;
    const elapsed = state.clock.elapsedTime;

    // Camera-up still needed below for halo placement.
    _camUp.setFromMatrixColumn(state.camera.matrixWorld, 1);

    // --- Guidance Edition Box ---
    if (guidanceRef.current) {
      // Lemniscate (figure-8) orbital drift, gated by visibility
      const ot = elapsed * ORBIT_SPEED;
      const driftX = Math.sin(ot) * ORBIT_RADIUS * s;
      const driftZ = Math.sin(ot * 2) * ORBIT_RADIUS * 0.5 * s;

      // Continuous float
      const floatY = Math.sin(elapsed * FLOAT_SPEED) * FLOAT_AMPLITUDE * s;

      guidanceRef.current.position.set(
        guidanceBasePos.x + driftX,
        guidanceBasePos.y + floatY,
        guidanceBasePos.z + driftZ,
      );

      // Idle rock only — no entrance tilt
      guidanceRef.current.rotation.set(
        guidanceBaseRot.x + Math.sin(elapsed * 0.3) * 0.04 * s,
        guidanceBaseRot.y + Math.cos(elapsed * 0.25) * 0.03 * s,
        guidanceBaseRot.z,
      );

      guidanceRef.current.scale.setScalar(sizeS * scalars.decksBoxBalance);
    }

    // --- Heavens Bridge Box (synchronous with Guidance) ---
    if (boxRef.current) {
      const ot = elapsed * ORBIT_SPEED + Math.PI;
      const driftX = Math.sin(ot) * ORBIT_RADIUS * s;
      const driftZ = Math.sin(ot * 2) * ORBIT_RADIUS * 0.5 * s;

      const floatY = Math.sin(elapsed * FLOAT_SPEED + Math.PI * 0.6) * FLOAT_AMPLITUDE * s;

      boxRef.current.position.set(
        boxBasePos.x + driftX,
        boxBasePos.y + floatY,
        boxBasePos.z + driftZ,
      );

      boxRef.current.rotation.set(
        boxBaseRot.x + Math.sin(elapsed * 0.3 + 1.5) * 0.04 * s,
        boxBaseRot.y + Math.cos(elapsed * 0.25 + 1) * 0.03 * s,
        boxBaseRot.z,
      );

      boxRef.current.scale.setScalar(sizeS);
    }

    // --- Halo planes (soft warm light pool beneath each box) ---
    // The vertical drop scales with the same per-box size factor as the box
    // itself (haloOffset is tuned against the full-size desktop box) — a
    // fixed drop leaves the halo stranded far below the shrunken mobile box.
    const haloS = ctl.haloEnabled ? sizeS : 0;
    if (guidanceGlowRef.current && guidanceRef.current) {
      const gp = guidanceRef.current.position;
      const guidanceDrop = ctl.haloOffset * sizeS * scalars.decksBoxBalance;
      guidanceGlowRef.current.position.set(
        gp.x - _camUp.x * guidanceDrop,
        gp.y - _camUp.y * guidanceDrop,
        gp.z - _camUp.z * guidanceDrop,
      );
      const guidanceHaloS = haloS * scalars.decksBoxBalance;
      guidanceGlowRef.current.scale.set(
        guidanceHaloS * (ctl.haloWidth / GLOW_PLANE[0]),
        guidanceHaloS * (ctl.haloHeight / GLOW_PLANE[1]),
        guidanceHaloS,
      );
      if (guidanceGlowMatRef.current) {
        const pulse = 0.85 + Math.sin(elapsed * 0.6) * 0.15;
        guidanceGlowMatRef.current.opacity = haloS * pulse * ctl.haloOpacityMul;
      }
    }
    if (boxGlowRef.current && boxRef.current) {
      const bp = boxRef.current.position;
      const boxDrop = ctl.haloOffset * sizeS;
      boxGlowRef.current.position.set(
        bp.x - _camUp.x * boxDrop,
        bp.y - _camUp.y * boxDrop,
        bp.z - _camUp.z * boxDrop,
      );
      boxGlowRef.current.scale.set(
        haloS * (ctl.haloWidth / GLOW_PLANE[0]),
        haloS * (ctl.haloHeight / GLOW_PLANE[1]),
        haloS,
      );
      if (boxGlowMatRef.current) {
        const pulse = 0.85 + Math.sin(elapsed * 0.6 + 1.4) * 0.15;
        boxGlowMatRef.current.opacity = haloS * pulse * ctl.haloOpacityMul;
      }
    }

    // --- 3-point lighting (driven by active preset, gated by visibility) ---
    if (keyLightRef.current) {
      keyLightRef.current.position.set(
        CENTER.x + ctl.keyX,
        CENTER.y + ctl.keyY,
        CENTER.z + ctl.keyZ,
      );
      keyLightRef.current.target.position.copy(CENTER);
      keyLightRef.current.target.updateMatrixWorld();
      keyLightRef.current.intensity = ctl.keyIntensity * s;
    }
    if (fillLightRef.current) {
      fillLightRef.current.position.set(
        CENTER.x + ctl.fillX,
        CENTER.y + ctl.fillY,
        CENTER.z + ctl.fillZ,
      );
      fillLightRef.current.intensity = ctl.fillIntensity * s;
      fillLightRef.current.distance = ctl.fillDistance;
    }
    if (rimLightRef.current) {
      rimLightRef.current.position.set(
        CENTER.x + ctl.rimX,
        CENTER.y + ctl.rimY,
        CENTER.z + ctl.rimZ,
      );
      rimLightRef.current.intensity = ctl.rimIntensity * s;
      rimLightRef.current.distance = ctl.rimDistance;
    }

    // Hide the deck boxes while a page transition is in flight so they don't
    // visibly scale-in / orbit under the fading snapshot. Glows, lights, and
    // the rest of the scene continue to animate normally.
    if (isTransitioningRef.current) {
      if (guidanceRef.current) guidanceRef.current.visible = false;
      if (boxRef.current) boxRef.current.visible = false;
    } else {
      if (guidanceRef.current) guidanceRef.current.visible = true;
      if (boxRef.current) boxRef.current.visible = true;
    }
  });

  const opacity = active ? 0.58 : 0.1;

  return (
    <group>
      {/* Clouds — lit Lambert material, baked from a hero-seeded Leva tune
          (see DECKS_CLOUDS). Position and the skewed distribution stay
          decks-specific so the clouds remain anchored around the boxes. */}
      <Clouds scale={cloud.scale} limit={segments} position={DECKS_CLOUDS_POS}>
        <Cloud
          segments={segments}
          seed={cloud.seed}
          volume={cloud.volume}
          growth={cloud.growth}
          smallestVolume={cloud.smallestVolume}
          bounds={cloud.bounds}
          opacity={opacity * cloud.opacityMul}
          speed={cloud.speed}
          color={cloud.color}
          concentrate="random"
          distribute={makeSphericalDistribute(spherical, {
            yMul: 0.75,
            yAdd: 0.2,
            zMul: 1.05,
          })}
        />
      </Clouds>

      {/* Pearl sparkles drifting between the boxes — atmospheric depth. */}
      <group position={[CENTER.x, CENTER.y, CENTER.z]}>
        <Sparkles
          count={SPARKLE_COUNT}
          scale={SPARKLE_SCALE}
          size={SPARKLE_SIZE}
          speed={SPARKLE_SPEED}
          opacity={SPARKLE_OPACITY}
          color={SPARKLE_COLOR}
        />
      </group>

      {/* Section title now lives in ScrollSections as an HTML overlay
          (typography from tokens.css: .t-hero + .t-hero--on-cloud). */}

      {/* Guidance Edition Box */}
      <group ref={guidanceRef} scale={0}>
        <GuidanceEditionBox fit={GUIDANCE_SCALE} envMapIntensity={ctl.envMapIntensity} />
      </group>

      {/* Heavens Bridge Box */}
      <group ref={boxRef} scale={0}>
        <HeavensBridgeBox fit={BOX_SCALE} envMapIntensity={ctl.envMapIntensity} />
      </group>

      {/* Warm halo planes — give each box a soft light pool to anchor it */}
      <group ref={guidanceGlowRef} scale={0}>
        <Billboard>
          <mesh renderOrder={2}>
            <planeGeometry args={GLOW_PLANE} />
            <meshBasicMaterial
              ref={guidanceGlowMatRef}
              map={glowTex}
              transparent
              opacity={0}
              blending={THREE.AdditiveBlending}
              depthWrite={false}
              toneMapped={false}
            />
          </mesh>
        </Billboard>
      </group>
      <group ref={boxGlowRef} scale={0}>
        <Billboard>
          <mesh renderOrder={2}>
            <planeGeometry args={GLOW_PLANE} />
            <meshBasicMaterial
              ref={boxGlowMatRef}
              map={glowTex}
              transparent
              opacity={0}
              blending={THREE.AdditiveBlending}
              depthWrite={false}
              toneMapped={false}
            />
          </mesh>
        </Billboard>
      </group>

      {/* IBL is mounted globally in Scene.tsx (preset depends on activeScene) */}
      <ambientLight intensity={ctl.ambientIntensity} />
      <hemisphereLight
        color={ctl.hemiSky}
        groundColor={ctl.hemiGround}
        intensity={ctl.hemiIntensity}
      />
      {/* Key directional — defines form */}
      <directionalLight ref={keyLightRef} color={ctl.keyColor} intensity={0} />
      {/* Fill point — softens shadow side */}
      <pointLight
        ref={fillLightRef}
        color={ctl.fillColor}
        intensity={0}
        distance={ctl.fillDistance}
        decay={1.5}
      />
      {/* Rim point — separates boxes from the cloud background */}
      <pointLight
        ref={rimLightRef}
        color={ctl.rimColor}
        intensity={0}
        distance={ctl.rimDistance}
        decay={1.4}
      />
    </group>
  );
}
